import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from '../components/DataTable'
import type { DataTableColumn } from '../components/DataTable'
import { StatCard } from '../components/StatCard'

/** Matches one row of `GET /api/audit` in `backend/src/routes/audit.ts`,
 *  which is `admin_audit` including the Task 1 before/after/correlation_id
 *  columns. */
interface AuditRow {
  id: number
  operator_email: string
  action: string
  target: string | null
  params_summary: unknown
  result: string | null
  before: unknown
  after: unknown
  correlation_id: string | null
  ts: string
}

interface AuditResponse {
  entries: AuditRow[]
  limit: number
  offset: number
  /** Full match count across every page, from `count(*) over ()`. */
  total?: number
}

const PAGE_SIZE = 50

type ResultFilter = 'any' | 'ok' | 'error'

interface Filters {
  operator: string
  resource: string
  action: string
  result: ResultFilter
  from: string
  to: string
}

const EMPTY_FILTERS: Filters = {
  operator: '',
  resource: '',
  action: '',
  result: 'any',
  from: '',
  to: '',
}

/**
 * The Sydney UTC offset for a given calendar date, as `+10:00` / `+11:00`.
 *
 * The date filter used to send `2026-08-28T00:00:00` with no offset at all.
 * The container parses a naive string as UTC, so a Sydney operator's
 * "28 August" was out by ten or eleven hours at BOTH ends — the first
 * morning's rows missing, the previous evening's included. Deriving the
 * offset from the date itself (rather than assuming +10:00) keeps this
 * correct across the daylight-saving boundary.
 */
export function sydneyOffset(isoDate: string): string {
  const probe = new Date(`${isoDate}T12:00:00Z`)
  if (Number.isNaN(probe.getTime())) {
    return '+10:00'
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Australia/Sydney',
    timeZoneName: 'longOffset',
  }).formatToParts(probe)
  const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+10:00'
  const match = /GMT([+-]\d{2}:\d{2})/.exec(name)
  return match?.[1] ?? '+10:00'
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (HTTP ${err.status})`
  }
  return err instanceof Error ? err.message : String(err)
}

function formatTs(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })
}

/** Short, human-scannable rendering of a jsonb column (params_summary,
 *  before, after) — the full value is still in the DOM (title attribute)
 *  for anyone who needs it, just not laid out in the cell. */
function summarize(value: unknown): string {
  if (value === null || value === undefined) {
    return '-'
  }
  const text = typeof value === 'string' ? value : JSON.stringify(value)
  return text.length > 100 ? `${text.slice(0, 97)}...` : text
}

/** Pretty-prints a jsonb value for the detail panel, or '-' when absent. */
function pretty(value: unknown): string {
  if (value === null || value === undefined) {
    return '-'
  }
  if (typeof value === 'string') {
    return value
  }
  return JSON.stringify(value, null, 2)
}

/** The operator-supplied justification, if any: `writeAudit` folds a grant's
 *  reason into `params_summary.reason` (there is no dedicated column). */
function extractReason(paramsSummary: unknown): string | null {
  if (paramsSummary && typeof paramsSummary === 'object' && !Array.isArray(paramsSummary)) {
    const reason = (paramsSummary as { reason?: unknown }).reason
    if (typeof reason === 'string' && reason.trim().length > 0) {
      return reason
    }
  }
  return null
}

export function buildQuery(filters: Filters, offset: number): string {
  const params = new URLSearchParams()
  if (filters.operator.trim()) {
    params.set('operator', filters.operator.trim())
  }
  if (filters.resource.trim()) {
    params.set('resource', filters.resource.trim())
  }
  if (filters.action.trim()) {
    params.set('action', filters.action.trim())
  }
  if (filters.result !== 'any') {
    params.set('result', filters.result)
  }
  if (filters.from) {
    // Date-only <input type=date> value: pin to the start of the day so the
    // whole "from" day is included — WITH an explicit Sydney offset, or the
    // container reinterprets the naive string as UTC and the operator's day
    // is out by ten hours.
    params.set('from', `${filters.from}T00:00:00${sydneyOffset(filters.from)}`)
  }
  if (filters.to) {
    // ...and pin "to" to the END of the day, so filtering "to 28 Aug" keeps
    // 28 Aug's entries instead of cutting off at midnight (they'd otherwise
    // all be > the bound and vanish).
    params.set('to', `${filters.to}T23:59:59.999${sydneyOffset(filters.to)}`)
  }
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return params.toString()
}

/**
 * The unified-admin-access-control Task 2 audit-log view: a filterable,
 * paginated read window onto `admin_audit` (operator, resource/target, and
 * date-range filters — all resolved server-side by `GET /api/audit`, never
 * client-filtered). Every operator-attributed mutation the panel makes
 * (budgets, keys, and from Phase 1 onward, access grants) shows up here.
 */
export function AuditLogPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  const [entries, setEntries] = useState<AuditRow[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [offset, setOffset] = useState(0)

  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS)
  const [actions, setActions] = useState<string[]>([])

  // Row drill-down: the full entry shown in a detail panel (same pattern as
  // the Budgets page's per-user modal).
  const [detail, setDetail] = useState<AuditRow | null>(null)

  const load = useCallback(async (filters: Filters, at: number) => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiJson<AuditResponse>(`/api/audit?${buildQuery(filters, at)}`)
      setEntries(data.entries)
      setTotal(data.total ?? null)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(applied, offset)
  }, [load, applied, offset])

  // The action filter's options come from the log itself, so a verb added by
  // a future route shows up without a second edit here.
  useEffect(() => {
    void (async () => {
      try {
        const data = await apiJson<{ actions: string[] }>('/api/audit/actions')
        setActions(data.actions ?? [])
      } catch {
        // A failed options read leaves the select empty; the free-text
        // filters still work, so this is not worth an error banner.
        setActions([])
      }
    })()
  }, [])

  /**
   * `?id=1234` opens that entry's drawer on load, so an audit row is a thing
   * you can paste into a Teams message and have someone else land on.
   * Read once, on mount: the deep link seeds the view, it does not fight
   * the operator's subsequent navigation.
   */
  useEffect(() => {
    const deepLinkId = new URLSearchParams(window.location.search).get('id')
    if (!deepLinkId) {
      return
    }
    void (async () => {
      try {
        const data = await apiJson<AuditResponse>(`/api/audit?id=${encodeURIComponent(deepLinkId)}&limit=1`)
        const first = data.entries[0]
        if (first) {
          setDetail(first)
        }
      } catch {
        // A bad or stale id simply opens nothing.
      }
    })()
  }, [])

  function submitFilters(event: FormEvent) {
    event.preventDefault()
    setOffset(0)
    setApplied(draft)
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS)
    setOffset(0)
    setApplied(EMPTY_FILTERS)
  }

  const hasActiveFilters =
    applied.operator !== '' ||
    applied.resource !== '' ||
    applied.action !== '' ||
    applied.result !== 'any' ||
    applied.from !== '' ||
    applied.to !== ''

  // Next is enabled by the TOTAL, not by "did this page come back full".
  // The old rule (`entries.length === PAGE_SIZE`) left both buttons dead
  // whenever the table held fewer than 50 rows, which is the reported bug.
  const canPageForward = total !== null ? offset + entries.length < total : entries.length === PAGE_SIZE

  /** Opens a row's drawer. Shared by the button and the row itself. */
  const openDetail = (row: AuditRow) => setDetail(row)

  const columns: Array<DataTableColumn<AuditRow>> = [
    { key: 'ts', header: 'Time', render: (row) => formatTs(row.ts), width: '11rem' },
    { key: 'operator', header: 'Operator', render: (row) => row.operator_email },
    { key: 'action', header: 'Action', render: (row) => row.action },
    { key: 'target', header: 'Resource', render: (row) => row.target ?? '-' },
    {
      key: 'result',
      header: 'Result',
      render: (row) =>
        row.result && row.result.startsWith('error') ? (
          <span style={{ color: tokens.danger }}>{row.result}</span>
        ) : (
          <span style={{ color: tokens.textSecondary }}>{row.result ?? '-'}</span>
        ),
    },
    {
      key: 'summary',
      header: 'Change',
      render: (row) => <span title={summarize(row.params_summary)}>{summarize(row.params_summary)}</span>,
    },
    {
      key: 'details',
      header: '',
      align: 'right',
      render: (row) => (
        <button type="button" style={styles.buttonGhost} onClick={() => openDetail(row)}>
          Details
        </button>
      ),
    },
  ]

  const totalPages = total !== null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : null

  return (
    <div style={styles.page}>
      <div style={styles.statsRow}>
        <StatCard
          label="Matching entries"
          value={total ?? '—'}
          hint={hasActiveFilters ? 'with these filters' : 'whole log'}
          tokens={tokens}
        />
        <StatCard label="Rows shown" value={entries.length} tokens={tokens} />
        <StatCard
          label="Page"
          value={`${Math.floor(offset / PAGE_SIZE) + 1}${totalPages ? ` / ${totalPages}` : ''}`}
          tokens={tokens}
        />
      </div>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Audit log</h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => void load(applied, offset)}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Every operator-attributed admin-panel change: budgets, keys, and access grants — filtered and paginated
          server-side. This log covers <strong>admin actions only</strong>; it never contains user chat content. No
          prompts or messages are stored anywhere on the platform (LiteLLM <code>store_prompts</code> is off and the
          analytics pipeline is titles-only). Click any row (or <strong>Details</strong>) for the full before/after
          grant sets, the reason recorded for sensitive-resource changes, and the correlation id. Dates are Sydney
          calendar days, sent to the server with an explicit offset. Source: <code>chugpt_analytics.admin_audit</code>.
        </p>

        <form onSubmit={submitFilters} style={styles.toolbar}>
          <input
            aria-label="Filter by operator"
            style={styles.input}
            placeholder="Operator email"
            value={draft.operator}
            onChange={(e) => setDraft((prev) => ({ ...prev, operator: e.target.value }))}
          />
          <input
            aria-label="Filter by resource"
            style={styles.input}
            placeholder="Resource / target"
            value={draft.resource}
            onChange={(e) => setDraft((prev) => ({ ...prev, resource: e.target.value }))}
          />
          <select
            aria-label="Filter by action"
            style={styles.input}
            value={draft.action}
            onChange={(e) => setDraft((prev) => ({ ...prev, action: e.target.value }))}
          >
            <option value="">Any action</option>
            {actions.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by result"
            style={styles.input}
            value={draft.result}
            onChange={(e) => setDraft((prev) => ({ ...prev, result: e.target.value as ResultFilter }))}
          >
            <option value="any">Any result</option>
            <option value="ok">ok</option>
            <option value="error">error / partial</option>
          </select>
          <input
            aria-label="From date"
            style={styles.input}
            type="date"
            value={draft.from}
            onChange={(e) => setDraft((prev) => ({ ...prev, from: e.target.value }))}
          />
          <input
            aria-label="To date"
            style={styles.input}
            type="date"
            value={draft.to}
            onChange={(e) => setDraft((prev) => ({ ...prev, to: e.target.value }))}
          />
          <button type="submit" style={styles.button} disabled={loading}>
            Filter
          </button>
          <button type="button" style={styles.buttonGhost} onClick={clearFilters} disabled={loading || !hasActiveFilters}>
            Clear
          </button>
        </form>

        {error ? <p style={styles.error}>{error}</p> : null}
        {loading ? (
          <p style={styles.hint}>Loading audit log…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={entries}
            getRowKey={(row) => String(row.id)}
            getRowLabel={(row) => `${row.action} by ${row.operator_email}`}
            tokens={tokens}
            emptyLabel="No audit entries match these filters."
            onRowActivate={openDetail}
          />
        )}

        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', alignItems: 'center' }}>
          <button
            type="button"
            style={styles.buttonGhost}
            disabled={loading || offset === 0}
            onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
          >
            Previous
          </button>
          <button
            type="button"
            style={styles.buttonGhost}
            disabled={loading || !canPageForward}
            onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
          >
            Next
          </button>
          <span style={styles.hint}>
            Showing {entries.length === 0 ? 0 : offset + 1}-{offset + entries.length}
            {total !== null ? ` of ${total.toLocaleString('en-AU')}` : ''}
          </span>
        </div>
      </section>

      {detail ? (
        <div style={styles.modalOverlay} role="presentation" onClick={() => setDetail(null)}>
          <div
            style={{ ...styles.modalCard, maxWidth: '46rem' }}
            role="dialog"
            aria-modal="true"
            aria-label={`Audit entry ${detail.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>{detail.action}</h3>
            <p style={styles.modalBody}>
              {detail.operator_email} · {formatTs(detail.ts)}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '8rem minmax(0,1fr)', gap: '0.35rem 0.9rem', fontSize: '0.85rem' }}>
              <span style={styles.fieldLabel}>Operator</span>
              <span style={{ color: tokens.textPrimary }}>{detail.operator_email}</span>
              <span style={styles.fieldLabel}>Action</span>
              <span style={{ color: tokens.textPrimary }}>{detail.action}</span>
              <span style={styles.fieldLabel}>Resource</span>
              <span style={{ color: tokens.textPrimary }}>{detail.target ?? '-'}</span>
              <span style={styles.fieldLabel}>Result</span>
              <span style={{ color: detail.result?.startsWith('error') ? tokens.danger : tokens.textPrimary }}>
                {detail.result ?? '-'}
              </span>
              <span style={styles.fieldLabel}>When</span>
              <span style={{ color: tokens.textPrimary }}>{formatTs(detail.ts)}</span>
              <span style={styles.fieldLabel}>Correlation</span>
              <span style={{ color: tokens.textSecondary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {detail.correlation_id ?? '-'}
              </span>
            </div>

            {extractReason(detail.params_summary) ? (
              <div style={{ ...styles.callout, marginTop: '1rem' }}>
                <div style={styles.fieldLabel}>Reason (sensitive-resource change)</div>
                <div style={{ color: tokens.textPrimary }}>{extractReason(detail.params_summary)}</div>
              </div>
            ) : null}

            <div style={{ ...styles.fieldLabel, marginTop: '1rem' }}>Change parameters</div>
            <pre style={styles.secretBox}>{pretty(detail.params_summary)}</pre>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '0.9rem', marginTop: '0.9rem' }}>
              <div>
                <div style={styles.fieldLabel}>Before</div>
                <pre style={styles.secretBox}>{pretty(detail.before)}</pre>
              </div>
              <div>
                <div style={styles.fieldLabel}>After</div>
                <pre style={styles.secretBox}>{pretty(detail.after)}</pre>
              </div>
            </div>

            {/* The deep link. An audit row you can paste into a Teams
                message and have the other person land on the same entry. */}
            <div style={{ ...styles.fieldLabel, marginTop: '1rem' }}>Link to this entry</div>
            <div style={styles.secretBox}>
              {`${window.location.origin}${window.location.pathname}?id=${detail.id}`}
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.buttonGhost}
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(`${window.location.origin}${window.location.pathname}?id=${detail.id}`)
                    .catch(() => undefined)
                }}
              >
                Copy link
              </button>
              <button type="button" style={styles.button} onClick={() => setDetail(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default AuditLogPage
